/**
 * RTK Query API Slice for Client/Organization Management
 * 
 * Handles all client/organization-related endpoints including:
 * - Client CRUD operations (list, create, update, delete)
 * - Client status management (active/inactive)
 * - Template and metric assignment
 * - Client statistics and user management
 * - Organization search functionality
 * 
 * Key architectural decisions:
 * - Uses apiClient as baseQuery (maintains snake_case)
 * - Appropriate caching for non-PHI organizational data
 * - Handles cascade operations carefully
 * - Supports role-based operations (admin endpoints)
 * - Maintains consistent error handling across all endpoints
 * 
 * Note: Clients are organizations (hospitals, clinics) that purchase the system
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

export const clientApi = createApi({
  reducerPath: 'clientApi',
  baseQuery: apiClientBaseQuery,
  
  // No caching for medical compliance (IEC 62304)
  keepUnusedDataFor: 0, // Zero caching across entire stack per architecture
  refetchOnMountOrArgChange: true,
  
  // Define cache tags for invalidation
  tagTypes: ['Client', 'ClientList', 'Template', 'Metric', 'ClientStatistics'],
  
  endpoints: (builder) => ({
    // Get paginated list of clients
    getClients: builder.query({
      query: (params = {}) => ({
        url: '/v1/clients',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search,
          is_active: params.is_active,
          sort: params.sort || 'name',
          order: params.order || 'asc',
          ...params,
        },
      }),
      transformResponse: (response, meta, arg) => {
        // Handle the client list structure
        // Response format: { status: true, data: [...] }
        // Map the response data to match expected field names
        const clients = (response.data || []).map(client => ({
          ...client,
          name: client.clientname || client.name,  // Map clientname to name for frontend compatibility
          clientname: client.clientname || client.name,  // Keep clientname for backwards compatibility
          primary_admin_email: client.admin_username || client.primary_admin_email,
          primary_admin_first_name: client.primary_admin_first_name || '',
          primary_admin_last_name: client.primary_admin_last_name || '',
        }));
        
        return {
          clients: clients,
          pagination: response.metadata?.pagination || {
            total: clients.length,
            limit: arg?.limit || 10,
            page: arg?.page || 1,
          },
        };
      },
      providesTags: (result) => 
        result?.clients
          ? [
              ...result.clients.map(({ id }) => ({ type: 'Client', id })),
              { type: 'ClientList', id: 'LIST' },
            ]
          : [{ type: 'ClientList', id: 'LIST' }],
    }),

    // Get single client by ID (requires admin)
    getClientById: builder.query({
      query: (params) => {
        // Handle both simple ID and params with lightweight flag
        const id = typeof params === 'object' ? params.id : params;
        const lightweight = typeof params === 'object' ? params.lightweight : false;

        return {
          url: `/v1/clients/${id}`,
          method: 'GET',
          params: lightweight ? { lightweight: true } : undefined,
        };
      },
      transformResponse: (response) => response.data || response,
      providesTags: (result, error, params) => {
        const id = typeof params === 'object' ? params.id : params;
        return [{ type: 'Client', id }];
      },
    }),

    // Get client details by ID (for client admins to get their own org)
    getClientDetailsById: builder.query({
      query: (id) => ({
        url: '/v1/get-clients-detail-by-id',
        method: 'POST',
        data: { company_key: id }, // This endpoint expects numeric ID in company_key field
      }),
      transformResponse: (response) => response.data || response,
      providesTags: (result, error, id) => [{ type: 'Client', id }],
    }),

    // Get client by company key (for public registration)
    // Supports both GET and POST methods
    getClientByKey: builder.mutation({
      query: (companyKey) => ({
        url: `/v1/client/${companyKey}`,
        method: 'GET',
        // This is a public endpoint, so we need to ensure no auth header is sent
        headers: {
          'X-Skip-Auth': 'true', // Signal to baseQuery to skip auth
        },
      }),
      transformResponse: (response) => {
        // The backend returns data object directly for GET requests
        return response.data || response;
      },
    }),
    
    // Legacy POST method for backward compatibility
    getClientByKeyPost: builder.mutation({
      query: (companyKey) => ({
        url: '/v1/get-client-registration-info',
        method: 'POST',
        data: { company_key: companyKey },
        // This is a public endpoint, so we need to ensure no auth header is sent
        headers: {
          'X-Skip-Auth': 'true', // Signal to baseQuery to skip auth
        },
      }),
      transformResponse: (response) => {
        // The backend returns data object directly
        return response.data || response;
      },
    }),

    // Create new client
    createClient: builder.mutation({
      query: (clientData) => ({
        url: '/v1/clients',
        method: 'POST',
        data: clientData, // Maintains snake_case from form
      }),
      invalidatesTags: [
        { type: 'ClientList', id: 'LIST' },
        'ClientStatistics',
      ],
    }),

    // Update client
    updateClient: builder.mutation({
      query: ({ id, ...clientData }) => ({
        url: `/v1/clients/${id}`,
        method: 'PUT',
        data: clientData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Client', id },
        { type: 'ClientList', id: 'LIST' },
        'ClientStatistics',
      ],
    }),

    // Delete client
    deleteClient: builder.mutation({
      query: (id) => ({
        url: `/v1/clients/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Client', id },
        { type: 'ClientList', id: 'LIST' },
        'ClientStatistics',
      ],
    }),

    // Get active clients only
    getActiveClients: builder.query({
      query: () => ({
        url: '/v1/clients/active',
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: (result) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Client', id })),
              { type: 'ClientList', id: 'ACTIVE' },
            ]
          : [{ type: 'ClientList', id: 'ACTIVE' }],
    }),

    // Toggle client status (active/inactive)
    toggleClientStatus: builder.mutation({
      query: (statusData) => ({
        url: '/v1/clients/toggle-status',
        method: 'POST',
        data: statusData, // Expects: { client_id, is_active }
      }),
      invalidatesTags: (result, error, { client_id }) => [
        { type: 'Client', id: client_id },
        { type: 'ClientList', id: 'LIST' },
        { type: 'ClientList', id: 'ACTIVE' },
      ],
    }),

    // Get templates assigned to client
    getClientTemplates: builder.query({
      query: (clientId) => ({
        url: `/v1/clients/templates/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: (result, error, clientId) => [
        { type: 'Template', id: `CLIENT_${clientId}` },
      ],
    }),

    // Assign templates to client
    assignTemplates: builder.mutation({
      query: (data) => ({
        url: '/v1/clients/assign-templates',
        method: 'POST',
        data: data, // Expects: { client_id, template_ids: [...] }
      }),
      invalidatesTags: (result, error, { client_id }) => [
        { type: 'Template', id: `CLIENT_${client_id}` },
        { type: 'Client', id: client_id },
      ],
    }),

    // Get metrics assigned to client
    getClientMetrics: builder.query({
      query: (clientId) => ({
        url: `/v1/clients/metrics/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: (result, error, clientId) => [
        { type: 'Metric', id: `CLIENT_${clientId}` },
      ],
    }),

    // Assign metrics to client
    assignMetrics: builder.mutation({
      query: (data) => ({
        url: '/v1/clients/assign-metrics',
        method: 'POST',
        data: data, // Expects: { client_id, metric_ids: [...] }
      }),
      invalidatesTags: (result, error, { client_id }) => [
        { type: 'Metric', id: `CLIENT_${client_id}` },
        { type: 'Client', id: client_id },
      ],
    }),

    // Get client statistics
    getClientStatistics: builder.query({
      query: (params = {}) => ({
        url: '/v1/clients/statistics',
        method: 'GET',
        params: {
          start_date: params.start_date,
          end_date: params.end_date,
          ...params,
        },
      }),
      transformResponse: (response) => response.data || {},
      providesTags: ['ClientStatistics'],
    }),

    // Search organizations by term (legacy endpoint)
    searchOrganizationsByTerm: builder.query({
      query: (searchTerm) => ({
        url: '/v1/get-org-by-term',
        method: 'POST',
        data: { term: searchTerm },
      }),
      transformResponse: (response) => {
        // Map the response data to match expected field names
        const organizations = (response.data || []).map(client => ({
          ...client,
          name: client.clientname || client.name,  // Map clientname to name for frontend compatibility
          clientname: client.clientname || client.name,  // Keep clientname for backwards compatibility
          primary_admin_email: client.admin_username || client.primary_admin_email,
          primary_admin_first_name: client.primary_admin_first_name || '',
          primary_admin_last_name: client.primary_admin_last_name || '',
        }));
        return organizations;
      },
      // No caching for organization search results (medical compliance)
      keepUnusedDataFor: 0, // No caching
    }),

    // Get users by client ID (legacy endpoint)
    getUsersByClientId: builder.query({
      query: (clientId) => ({
        url: '/v1/get-users-by-id',
        method: 'GET',
        params: { client_id: clientId },
      }),
      transformResponse: (response) => response.data || [],
      // Users are PHI, no caching for medical compliance
      keepUnusedDataFor: 0, // No caching for medical compliance
      providesTags: (result, error, clientId) => [
        { type: 'ClientUsers', id: clientId },
      ],
    }),

    // Bulk update client settings
    bulkUpdateClients: builder.mutation({
      query: (bulkData) => ({
        url: '/v1/clients/bulk-update',
        method: 'POST',
        data: bulkData, // Expects: { client_ids: [...], updates: {...} }
      }),
      invalidatesTags: [
        { type: 'ClientList', id: 'LIST' },
        'ClientStatistics',
      ],
    }),

    // Export clients (returns CSV/Excel)
    exportClients: builder.query({
      query: (params = {}) => ({
        url: '/v1/clients/export',
        method: 'GET',
        params: {
          format: params.format || 'csv',
          is_active: params.is_active,
          ...params,
        },
      }),
      // Don't cache export operations
      keepUnusedDataFor: 0,
    }),

    // Get client activity log
    getClientActivity: builder.query({
      query: ({ clientId, ...params }) => ({
        url: `/v1/clients/${clientId}/activity`,
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          ...params,
        },
      }),
      transformResponse: (response) => response.data || [],
      // Don't cache activity logs (audit trail)
      keepUnusedDataFor: 0,
    }),

    // Get client usage statistics
    getClientUsage: builder.query({
      query: ({ clientId, ...params }) => ({
        url: `/v1/clients/${clientId}/usage`,
        method: 'GET',
        params: {
          start_date: params.start_date,
          end_date: params.end_date,
          ...params,
        },
      }),
      transformResponse: (response) => response.data || {},
      // No caching for usage stats (may contain PHI-related counts)
      keepUnusedDataFor: 0, // No caching for medical compliance
      providesTags: (result, error, { clientId }) => [
        { type: 'ClientUsage', id: clientId },
      ],
    }),

    // Create client with admin user (transaction)
    createClientWithAdmin: builder.mutation({
      query: (data) => ({
        url: '/v1/clients/create-with-admin',
        method: 'POST',
        data: data, // Expects: { client_data: {...}, admin_data: {...} }
      }),
      invalidatesTags: [
        { type: 'ClientList', id: 'LIST' },
        'ClientStatistics',
      ],
    }),

    // Get all templates (for assignment)
    getAllTemplates: builder.query({
      query: () => ({
        url: '/v1/get-all-templates',
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: ['Template'],
    }),

    // Get all metrics (for assignment)
    getAllMetrics: builder.query({
      query: () => ({
        url: '/v1/get-all-metrics',
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: ['Metric'],
    }),


    // Validate client name uniqueness
    validateClientName: builder.query({
      query: (name) => ({
        url: '/v1/clients/validate-name',
        method: 'GET',
        params: { name },
      }),
      transformResponse: (response) => response.data || { is_unique: false },
      // Don't cache validation results
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetClientsQuery,
  useGetClientByIdQuery,
  useGetClientDetailsByIdQuery,
  useGetClientByKeyQuery,
  useGetActiveClientsQuery,
  useGetClientTemplatesQuery,
  useGetClientMetricsQuery,
  useGetClientStatisticsQuery,
  useSearchOrganizationsByTermQuery,
  useGetUsersByClientIdQuery,
  useExportClientsQuery,
  useGetClientActivityQuery,
  useGetClientUsageQuery,
  useGetAllTemplatesQuery,
  useGetAllMetricsQuery,
  useValidateClientNameQuery,
  
  // Mutations
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useToggleClientStatusMutation,
  useAssignTemplatesMutation,
  useAssignMetricsMutation,
  useBulkUpdateClientsMutation,
  useCreateClientWithAdminMutation,
  useGetClientByKeyMutation,
  useGetClientByKeyPostMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetClientsQuery,
  useLazyExportClientsQuery,
  useLazySearchOrganizationsByTermQuery,
  useLazyValidateClientNameQuery,
  
  // For manual cache invalidation if needed
  util: { invalidateTags },
} = clientApi;

export default clientApi;